import { useEffect, useState } from "react";
import { toast } from "sonner";
import { QrCode as QrIcon, Download, ExternalLink, Copy, Printer, Share2, FileText } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export default function QRCard() {
    const [doctor, setDoctor] = useState(null);
    const [preview, setPreview] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        Promise.all([
            api.get("/me/doctor"),
            api.get("/publish/preview-url").catch(() => ({ data: null })),
        ]).then(([dr, pv]) => {
            setDoctor(dr.data);
            setPreview(pv.data);
        }).catch((e) => toast.error(errMsg(e, "Failed to load")));
    }, []);

    const isPublished = doctor?.profile_status === "published";
    const publicUrl = preview?.public_url;
    const qrUrl = preview?.qr_url;
    const cardUrl = preview?.card_url;
    const cardHtmlUrl = preview?.card_html_url;

    function copy() {
        if (!publicUrl) return;
        navigator.clipboard.writeText(publicUrl).then(() => {
            setCopied(true);
            toast.success("Public URL copied");
            setTimeout(() => setCopied(false), 2000);
        });
    }

    async function share() {
        if (!publicUrl) return;
        try {
            if (navigator.share) {
                await navigator.share({
                    title: `Dr. ${doctor?.full_name} — Digital Identity`,
                    text: `Visit my verified digital identity`,
                    url: publicUrl,
                });
            } else {
                copy();
            }
        } catch {}
    }

    async function downloadPoster(size) {
        try {
            const resp = await api.get(`/publish/qr-poster.pdf?size=${size}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `qr-poster-${size}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`${size.toUpperCase()} poster downloaded`);
        } catch (e) {
            toast.error(errMsg(e, "Download failed"));
        }
    }

    return (
        <DoctorShell doctor={doctor}>
            <div className="mb-6" data-testid="qrcard-header">
                <h1 className="ddi-section-title">QR Code & Digital Visiting Card</h1>
                <p className="ddi-section-sub">
                    Every doctor gets a portable QR that opens their verified public profile. Print your digital card on prescription slips or hand it out from your phone.
                </p>
            </div>

            {!isPublished && (
                <Card className="border-[#F59E0B]/30 bg-[#FEF3C7] mb-6">
                    <CardContent className="py-4 flex items-center gap-3">
                        <div className="text-[13px] font-semibold text-[#92400E]">Publish your profile first</div>
                        <div className="text-[12px] text-[#92400E]/80 flex-1">Your QR and card become available once your public profile is live.</div>
                        <Button onClick={() => window.location.href = "/doctor/publish"} className="bg-[#3A80F7] hover:bg-[#2D6BE0]" data-testid="qrcard-publish-cta">Open publish</Button>
                    </CardContent>
                </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* QR panel */}
                <Card className="border-[#E5E9F2] ddi-shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2"><QrIcon size={18} className="text-[#3A80F7]" /> QR Code</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {qrUrl && isPublished ? (
                            <div className="text-center">
                                <div className="inline-block p-4 bg-white border border-[#E5E9F2] rounded-2xl ddi-shadow-sm mb-3">
                                    <img src={qrUrl} alt="QR code" className="w-52 h-52" data-testid="qrcard-qr-img" />
                                </div>
                                <div>
                                    <a href={qrUrl} download="doctor-qr.png" className="ddi-btn-primary text-[13px] inline-flex" data-testid="qrcard-qr-download">
                                        <Download size={14} /> Download PNG
                                    </a>
                                </div>
                            </div>
                        ) : (
                            <div className="text-[13px] text-[#64748B] text-center py-8">
                                <QrIcon size={40} className="mx-auto text-[#94A3B8] mb-3" />
                                QR appears after publish.
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Public URL + share */}
                <Card className="border-[#E5E9F2] ddi-shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2"><Share2 size={18} className="text-[#3A80F7]" /> Share your identity</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-[#64748B] mb-1 font-semibold">Public URL</div>
                            {publicUrl ? (
                                <div className="flex items-center gap-2 bg-[#F5F7FB] rounded-lg p-2.5 border border-[#E5E9F2]">
                                    <a href={publicUrl} target="_blank" rel="noopener" className="text-[13px] text-[#3A80F7] hover:underline truncate flex-1 font-mono" data-testid="qrcard-public-url">
                                        {publicUrl.replace(/^https?:\/\//, "")}
                                    </a>
                                    <button onClick={copy} className="p-1.5 hover:bg-white rounded-md text-[#64748B] hover:text-[#3A80F7]" data-testid="qrcard-copy-url">
                                        <Copy size={14} />
                                    </button>
                                    <a href={publicUrl} target="_blank" rel="noopener" className="p-1.5 hover:bg-white rounded-md text-[#64748B] hover:text-[#3A80F7]">
                                        <ExternalLink size={14} />
                                    </a>
                                </div>
                            ) : (
                                <div className="text-[13px] text-[#94A3B8]">Not published yet.</div>
                            )}
                            {copied && <div className="text-[11px] text-[#16A34A] mt-1">Copied!</div>}
                        </div>
                        {publicUrl && (
                            <Button onClick={share} variant="outline" className="w-full border-[#3A80F7] text-[#3A80F7] hover:bg-[#EEF4FE]" data-testid="qrcard-share">
                                <Share2 size={14} className="mr-2" /> Share (native)
                            </Button>
                        )}
                        {cardUrl && isPublished && (
                            <a href={cardUrl} target="_blank" rel="noopener" className="ddi-btn-primary w-full justify-center text-[13px]" data-testid="qrcard-open-card">
                                <ExternalLink size={14} /> Open card page
                            </a>
                        )}
                    </CardContent>
                </Card>

                {/* Print instructions */}
                <Card className="border-[#E5E9F2] ddi-shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2"><Printer size={18} className="text-[#3A80F7]" /> Print your card</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ol className="text-[13px] text-[#334155] space-y-2 list-decimal pl-4">
                            <li>Open your digital card page.</li>
                            <li>Use the browser Print (⌘/Ctrl + P).</li>
                            <li>Save as PDF or print on card stock.</li>
                        </ol>
                        {cardHtmlUrl && isPublished ? (
                            <a href={cardHtmlUrl} target="_blank" rel="noopener" className="ddi-btn-outline w-full justify-center text-[13px] mt-4" data-testid="qrcard-open-print">
                                <Printer size={14} /> Open printable card
                            </a>
                        ) : (
                            <div className="text-[12px] text-[#94A3B8] mt-3">Available after publish.</div>
                        )}
                        {/* Batch 1 · Prescription-ready QR poster */}
                        {isPublished && (
                            <div className="mt-4 pt-4 border-t border-[#E5E9F2]" data-testid="qr-poster-block">
                                <div className="text-[11px] uppercase tracking-widest text-[#64748B] font-semibold mb-2 flex items-center gap-1">
                                    <FileText size={12} /> Prescription-ready poster
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="outline" className="flex-1 text-[12px] border-[#3A80F7] text-[#3A80F7] hover:bg-[#EEF4FE]"
                                        onClick={() => downloadPoster("a4")} data-testid="qr-poster-a4">
                                        <Download size={12} className="mr-1" /> A4 PDF
                                    </Button>
                                    <Button variant="outline" className="flex-1 text-[12px] border-[#3A80F7] text-[#3A80F7] hover:bg-[#EEF4FE]"
                                        onClick={() => downloadPoster("a5")} data-testid="qr-poster-a5">
                                        <Download size={12} className="mr-1" /> A5 PDF
                                    </Button>
                                </div>
                                <p className="text-[11px] text-[#64748B] mt-2">
                                    Branded poster with your QR, portrait and clinic details — print and tape next to your prescription pad.
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Card preview iframe */}
            {isPublished && cardHtmlUrl && (
                <Card className="border-[#E5E9F2] mt-6 ddi-shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base">Live preview</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <iframe
                            src={cardHtmlUrl}
                            title="Digital card preview"
                            className="w-full h-[700px] rounded-b-xl bg-white"
                            data-testid="qrcard-preview-iframe"
                        />
                    </CardContent>
                </Card>
            )}
        </DoctorShell>
    );
}
